'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';

import type { Person } from './types/person';
import { useFormContext } from './providers/form-context';

export type FlowStep = {
  key: string;
  label: string;
  pathRegex: RegExp;
  estimatedMinutes: number;
  borrowerIndex?: number;
};

function firstNameOrDefault(person: Person, fallback: string) {
  const first = person.form.memberInfo.basicInfo.first?.trim();
  return first && first.length > 0 ? first : fallback;
}

function buildFlowSteps(people: Person[]): FlowStep[] {
  const coBorrowers = people
    .filter((p) => p.isBorrower && p.index !== 0)
    .sort((a, b) => a.index - b.index);

  const steps: FlowStep[] = [
    {
      key: 'loan-type',
      label: 'Loan type',
      pathRegex: /^\/loan-selection/,
      estimatedMinutes: 1,
    },
    {
      key: 'primary-personal',
      label: 'About you',
      pathRegex: /^\/0\/personal-info/,
      estimatedMinutes: 2,
      borrowerIndex: 0,
    },
    {
      key: 'primary-residence',
      label: 'Where you live',
      pathRegex: /^\/0\/residence-info/,
      estimatedMinutes: 1,
      borrowerIndex: 0,
    },
    {
      key: 'primary-employment',
      label: 'Work & income',
      pathRegex: /^\/0\/employment/,
      estimatedMinutes: 2,
      borrowerIndex: 0,
    },
    {
      key: 'co-borrower-selection',
      label: 'Co-borrower',
      pathRegex: /^\/co-borrower/,
      estimatedMinutes: 1,
    },
  ];

  coBorrowers.forEach((person, i) => {
    const fallback = coBorrowers.length > 1 ? `Co-borrower ${i + 1}` : 'Co-borrower';
    const name = firstNameOrDefault(person, fallback);
    steps.push(
      {
        key: `coborrower-${person.index}-personal`,
        label: `About ${name}`,
        pathRegex: new RegExp(`^/${person.index}/personal-info`),
        estimatedMinutes: 2,
        borrowerIndex: person.index,
      },
      {
        key: `coborrower-${person.index}-residence`,
        label: `${name}'s home`,
        pathRegex: new RegExp(`^/${person.index}/residence-info`),
        estimatedMinutes: 1,
        borrowerIndex: person.index,
      },
      {
        key: `coborrower-${person.index}-employment`,
        label: `${name}'s work`,
        pathRegex: new RegExp(`^/${person.index}/employment`),
        estimatedMinutes: 2,
        borrowerIndex: person.index,
      }
    );
  });

  steps.push(
    {
      key: 'references',
      label: 'References',
      pathRegex: /^\/references/,
      estimatedMinutes: 1,
    },
    {
      key: 'loan-details',
      label: 'Loan details',
      pathRegex: /^\/loan-details/,
      estimatedMinutes: 2,
    },
    {
      key: 'summary',
      label: 'Review & submit',
      pathRegex: /^\/summary/,
      estimatedMinutes: 2,
    },
    {
      key: 'confirmation',
      label: 'All done',
      pathRegex: /^\/confirmation/,
      estimatedMinutes: 0,
    }
  );

  return steps;
}

function useFlowSteps(): FlowStep[] {
  const { people } = useFormContext();
  return useMemo(() => buildFlowSteps(people), [people]);
}

export function useCurrentFlowStep() {
  const pathname = usePathname();
  const steps = useFlowSteps();
  return useMemo(() => {
    const index = steps.findIndex((s) => s.pathRegex.test(pathname));
    if (index === -1) return null;
    return { step: steps[index]!, index, steps, total: steps.length };
  }, [pathname, steps]);
}

export function minutesRemainingFrom(steps: FlowStep[], index: number): number {
  return steps.slice(index).reduce((sum, step) => sum + step.estimatedMinutes, 0);
}
